import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { useTranslate } from 'src/locales';

import { CandidateSearchStreamState } from '../hooks/use-candidate-search-stream';

interface StreamedSearchStatusProps {
  search: CandidateSearchStreamState;
  /** Hide the line entirely, e.g. before any search has run or when the search failed outright. */
  hidden?: boolean;
}

/**
 * The line above a streamed search's results (JT1-1802): "Waiting to start…", then a progress bar
 * with "Searching… N found so far" and "X of Y checked", then the total — or "1,000+" with a hint to
 * narrow the search. Shared by the advanced search and the keyword box so both say the same thing.
 */
export default function StreamedSearchStatus({ search, hidden }: StreamedSearchStatusProps) {
  const { t } = useTranslate();

  const found = search.rows.length;
  const queued = search.status === 'queued';
  const searching = search.status === 'searching' || queued;
  const progress = search.total ? Math.min(100, (search.checked / search.total) * 100) : 0;

  const summary = (() => {
    if (hidden) return '';
    if (queued) return t('candidates.search.queued');
    if (searching) return t('candidates.search.searching', { found });
    if (search.capped) return t('candidates.search.capped', { count: found });
    return t('candidates.search.results', { count: found });
  })();

  return (
    <Stack spacing={0.75} sx={{ minWidth: 240, flexGrow: 1, maxWidth: 480 }}>
      <Typography variant="subtitle2" data-testid="search-summary">{summary}</Typography>
      {!hidden && searching && (
        <>
          <LinearProgress
            variant={search.total ? 'determinate' : 'indeterminate'}
            value={progress}
            aria-label={t('candidates.search.searching', { found })}
          />
          {!!search.total && (
            <Typography variant="caption" color="text.secondary">
              {t('candidates.search.searching_progress', {
                checked: search.checked.toLocaleString(),
                total: search.total.toLocaleString(),
              })}
            </Typography>
          )}
        </>
      )}
      {!hidden && search.status === 'done' && search.capped && (
        <Typography variant="caption" color="text.secondary">
          {t('candidates.search.capped_hint', { count: found })}
        </Typography>
      )}
    </Stack>
  );
}
